// RangierbahnhofAbgangsRadar — Instrument I5 (Auftrag K6).
//
// Szene nach Cowork-Referenz-Widget "rangierbahnhof_abgangs_radar" (03.07.):
// 5 Gleise (4 Firmen in Markenfarben + Konzern-Summe), Abbuchungs-Portal mit
// Signal links, Ablaufberg + Stellwerk rechts, Zeitachse HEUTE/+7/+14/+30.
// Güterwagen = anstehende Abgänge (Posten) aus dem K5-Endpoint
// GET /api/v1/outflow/upcoming: Breite ∝ √Betrag, Countdown-Schild,
// Quellen-Marker ● (Daemon/belegt) · Ø (searchReceipts/geschätzt) · ▢ (manuell).
//
// SCAFFOLD (K-INTEGRATION: components/instrumente/ = eigene Datei, Session B):
// korrekte Datenbindung + Struktur + Motion am ZENTRALEN Ticker (lib/motion/
// ticker.ts — KEIN eigener rAF). Die Motion-Feinpolitur (Abstoß-Anticipation,
// Ausroll-ease-out, Puffer-Recoil + gedämpftes Nicken, Rad-Rotation ∝ Distanz,
// ICE-Trail) macht die Cowork-Screenshot-Session; hier ist sie als sanftes
// Idle-Nicken + Überfällig-Puls (nur Signal-Clay) angelegt.
//
// R1: InfraX hat ein EIGENES Gleis (Einzeldaten sichtbar), fließt aber NICHT
// in die Konzern-Summe (Whitelist xholding/drainx/gundk).
//
// Vorbedingungen (Stand K6-Bau): der K5-Endpoint existiert noch nicht →
// useOutflowUpcoming rendert graceful die 4 Daten-Kontrakt-Zustände
// (loading/error/missing/ok). Migration auf useDatenKontrakt(quelle), sobald
// die shared Infra steht. Einbau ins Grid macht der Integrator (Sperrzone).

import { useEffect, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";

import { apiFetch, formatEUR } from "../../lib/api";
import { formatEurCompact } from "../../lib/bwa-aggregate";
import { FIRMA_COLORS, type FirmaSlug } from "../../lib/firma-colors";
import { subscribe } from "../../lib/motion/ticker";

// --- K5-Kontrakt (GET /api/v1/outflow/upcoming) — lokal definiert, bis die
//     shared Typen stehen; Session C implementiert den Endpoint passend. -----

export type OutflowQuelle = "daemon" | "searchreceipts" | "manual";

export interface OutflowPosten {
  id: string;
  firma: FirmaSlug;
  /** Abgangsbetrag in EUR (> 0). */
  betrag_eur: number;
  /** Fälligkeit ISO-Datum (YYYY-MM-DD). */
  faellig_am: string;
  titel?: string | null;
  quelle: OutflowQuelle;
}

export interface OutflowUpcoming {
  posten: OutflowPosten[];
  stand_iso?: string | null;
}

// Klarheit-Umbau (Kevin 06.07.): EIN Gleis je Firma, KEIN Konzern-Gleis mehr
// — das duplizierte jeden Wagen und machte den Bahnhof unlesbar. Die
// Konzern-Summe steht jetzt als EINE Zahl in der Kopfzeile.
// R1: Konzern-Summe = drainx+gundk+xholding (OHNE InfraX); InfraX-Gleis
// zeigt seine Wagen, zählt aber nicht in die Kopf-Summe.
const KONZERN_WHITELIST: readonly FirmaSlug[] = ["xholding", "drainx", "gundk"];
const GLEISE: readonly { key: FirmaSlug; label: string; hex: string }[] = [
  { key: "drainx", label: "DrainX", hex: FIRMA_COLORS.drainx.hex },
  { key: "gundk", label: "G+K", hex: FIRMA_COLORS.gundk.hex },
  { key: "xholding", label: "X-Holding", hex: FIRMA_COLORS.xholding.hex },
  { key: "infrax", label: "InfraX", hex: FIRMA_COLORS.infrax.hex },
];

// --- Geometrie -------------------------------------------------------------
const VB_W = 620;
const VB_H = 210;
const X_LABEL = 8; // Firmen-Label + Teilsumme links
const X_UEBERF = 92; // Überfällig-Zone (links von HEUTE)
const X_HEUTE = 128; // Start der Zeitachse (HEUTE)
const X_END = 556; // rechtes Ende (Horizont)
const LANE_TOP = 30;
const LANE_H = 40;
const HORIZONTE = [7, 14, 30] as const;

const clamp = (v: number, lo: number, hi: number): number => Math.min(hi, Math.max(lo, v));

function tageBis(iso: string, heute: Date): number {
  const d = new Date(iso + "T00:00:00");
  return Math.round((d.getTime() - heute.getTime()) / 86_400_000);
}

/** Wagenbreite ∝ √Betrag, auf sinnvolle Pixel geklemmt. */
function wagenBreite(betrag: number): number {
  return clamp(Math.sqrt(Math.max(betrag, 0)) * 0.9, 14, 64);
}

interface AbgeleiteterWagen {
  posten: OutflowPosten;
  gleis: FirmaSlug;
  x: number;
  breite: number;
  tage: number;
  ueberfaellig: boolean;
}

export default function RangierbahnhofAbgangsRadar() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["outflow-upcoming"],
    queryFn: () => apiFetch<OutflowUpcoming>("/api/v1/outflow/upcoming"),
    // Live-Reaktivität (Auftrag K7): kommt vom gemeinsamen Query-Refresh.
    staleTime: 60_000,
    retry: 1,
  });

  const heute = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  // Auto-Horizont-Flip 7/14/30 (keine Buttons): kleinster Horizont, der ≥80 %
  // der anstehenden (nicht überfälligen) Posten fasst.
  const { horizont, wagen, konzernSumme, konzernCount, summeProFirma } = useMemo(() => {
    const posten = data?.posten ?? [];
    const kuenftig = posten
      .map((p) => tageBis(p.faellig_am, heute))
      .filter((t) => t >= 0)
      .sort((a, b) => a - b);
    let h: number = HORIZONTE[HORIZONTE.length - 1];
    if (kuenftig.length > 0) {
      const idx = Math.floor(kuenftig.length * 0.8);
      const p80 = kuenftig[Math.min(idx, kuenftig.length - 1)];
      h = HORIZONTE.find((cand) => cand >= p80) ?? HORIZONTE[HORIZONTE.length - 1];
    }

    const xFor = (tage: number) => X_HEUTE + (X_END - X_HEUTE) * clamp(tage / h, 0, 1);

    // EIN Wagen je Posten (kein Konzern-Duplikat mehr). Überfällige stehen
    // in der Überfällig-Zone links von HEUTE.
    const derived: AbgeleiteterWagen[] = posten.map((p) => {
      const tage = tageBis(p.faellig_am, heute);
      const ueberfaellig = tage < 0;
      return {
        posten: p,
        gleis: p.firma,
        x: ueberfaellig ? X_UEBERF : xFor(tage),
        breite: wagenBreite(p.betrag_eur),
        tage,
        ueberfaellig,
      };
    });
    derived.sort((a, b) => b.posten.betrag_eur - a.posten.betrag_eur);

    // Kopf-Summe = Konzern (R1: OHNE InfraX). Teilsummen je Firma für die
    // Gleis-Labels.
    const proFirma: Record<string, number> = {};
    let kSum = 0;
    let kCount = 0;
    for (const p of posten) {
      proFirma[p.firma] = (proFirma[p.firma] ?? 0) + p.betrag_eur;
      if (KONZERN_WHITELIST.includes(p.firma)) {
        kSum += p.betrag_eur;
        kCount += 1;
      }
    }
    return {
      horizont: h,
      wagen: derived,
      konzernSumme: kSum,
      konzernCount: kCount,
      summeProFirma: proFirma,
    };
  }, [data, heute]);

  // Motion am ZENTRALEN Ticker (kein eigener rAF). Imperativ über Refs → kein
  // React-Re-Render pro Frame, nur transform/opacity. reduced-motion: Ticker
  // startet nicht → statisch. Werte kalibriert aus dem Referenz-Widget
  // (Kevin 03.07.), alles transient.
  const wagenRefs = useRef<Array<SVGGElement | null>>([]);
  const wheelRefs = useRef<Array<[SVGGElement | null, SVGGElement | null]>>([]);
  const pulsRef = useRef<SVGCircleElement | null>(null);
  const startTsRef = useRef<number | null>(null);
  const idleRef = useRef<Array<{ next: number; start: number | null }>>([]);
  const hatUeberfaellig = useMemo(() => wagen.some((w) => w.ueberfaellig), [wagen]);

  useEffect(() => {
    startTsRef.current = null;
    // Idle-Scheduler je Wagen: erster Check nach Ankunft (1400) + Recoil (620)
    // + 4,2–8 s.
    idleRef.current = wagen.map(() => ({
      next: 1400 + 620 + 4200 + Math.random() * 3800,
      start: null,
    }));

    const easeOutCubic = (p: number) => 1 - Math.pow(1 - p, 3);

    const unsub = subscribe((now) => {
      if (startTsRef.current === null) startTsRef.current = now;
      const elapsed = now - startTsRef.current;

      for (let i = 0; i < wagenRefs.current.length; i++) {
        const g = wagenRefs.current[i];
        const w = wagen[i];
        if (!g || !w) continue;
        const D = Math.max(0, w.x - X_HEUTE); // Rollstrecke in px
        const lt = elapsed - i * 60; // 60 ms Stagger pro Wagen

        let dx = 0;
        let deg = 0;
        let traveled = 0;

        if (lt < 0) {
          dx = -D; // wartet am Portal
        } else if (lt < 1400) {
          // Ausrollen: cubic ease-out 1400 ms + Abstoß-Anticipation
          // (−1,2 % der Rollstrecke, Sinus-Halbwelle über die ersten 6 %).
          const p = lt / 1400;
          const ease = easeOutCubic(p);
          const anti = p < 0.06 ? 0.012 * Math.sin((Math.PI * p) / 0.06) : 0;
          dx = -D * (1 - ease) - D * anti;
          traveled = D + dx;
        } else {
          const s = (lt - 1400) / 1000; // Sekunden seit Ankunft
          traveled = D;
          if (s < 0.62) {
            // Puffer-Recoil 3,2 px · e^(−4,5s) · sin(10πs) + Nicken
            // 0,9° · e^(−3,5s) · sin(14πs), beide über 620 ms.
            dx = 3.2 * Math.exp(-4.5 * s) * Math.sin(10 * Math.PI * s);
            deg = 0.9 * Math.exp(-3.5 * s) * Math.sin(14 * Math.PI * s);
          } else {
            // Idle-Nicken: 0,5°-Impuls über 500 ms, alle 4,2–8 s, 35 %.
            const st = idleRef.current[i];
            if (st) {
              if (st.start === null && elapsed >= st.next) {
                if (Math.random() < 0.35) st.start = elapsed;
                st.next = elapsed + 4200 + Math.random() * 3800;
              }
              if (st.start !== null) {
                const ip = (elapsed - st.start) / 500;
                if (ip >= 1) st.start = null;
                else deg = 0.5 * Math.sin(Math.PI * ip);
              }
            }
          }
        }

        // Nicken rotiert um den Rad-Aufstandspunkt (transform-origin unten).
        g.style.transform = `translate(${dx.toFixed(2)}px, 0) rotate(${deg.toFixed(3)}deg)`;

        // Rad-Rotation = zurückgelegte Distanz · 12,4°/px (an Position gekoppelt).
        const wheelDeg = (traveled * 12.4).toFixed(1);
        const wr = wheelRefs.current[i];
        if (wr?.[0]) wr[0].style.transform = `rotate(${wheelDeg}deg)`;
        if (wr?.[1]) wr[1].style.transform = `rotate(${wheelDeg}deg)`;
      }

      if (pulsRef.current) {
        const puls = 0.35 + 0.35 * (0.5 + 0.5 * Math.sin((now / 1000) * 3.2));
        pulsRef.current.style.opacity = puls.toFixed(2);
      }
    });
    return unsub;
  }, [wagen]);

  // --- Daten-Kontrakt-Zustände (K-INTEGRATION §3) ---
  if (isLoading) return <Rahmen hint="Abgänge laden …" />;
  if (isError) return <Rahmen hint="Abgänge derzeit nicht verfügbar" />;
  if (wagen.length === 0) {
    return <Rahmen hint="Keine anstehenden Abbuchungen im Horizont" leer />;
  }

  const clayHex = "#d97757";

  return (
    <div aria-label="Abgangs-Radar Rangierbahnhof (anstehende Abbuchungen)">
      <header className="flex items-baseline justify-between gap-2 px-1">
        <div className="label-tracked">Abgänge · nächste {horizont} Tage</div>
        <div className="num-mono text-sm font-semibold text-signal-clay">
          −{formatEurCompact(konzernSumme)}
          <span className="text-[10px] font-normal text-leitstand-dim">
            {" "}
            · {konzernCount} Abbuchungen
          </span>
        </div>
      </header>
      {/* Lesehilfe — sagt in einer Zeile, WIE man den Bahnhof liest. */}
      <p className="text-[10px] text-leitstand-dim mb-0.5 px-1">
        Jeder Wagen = eine Abbuchung auf ihrem Fälligkeitstag · Summe je Firma
        an der Lok · links der HEUTE-Linie = überfällig · InfraX zählt nicht in
        die Kopf-Summe (R1).
      </p>

      <svg viewBox={`0 0 ${VB_W} ${VB_H}`} width="100%" role="img" aria-hidden>
        {/* "Diese Woche"-Band (HEUTE..+7) dezent hinterlegt. */}
        {horizont >= 7 && (
          <rect
            x={X_HEUTE}
            y={LANE_TOP - 2}
            width={(X_END - X_HEUTE) * (7 / horizont)}
            height={GLEISE.length * LANE_H}
            fill="rgba(245,241,232,0.03)"
          />
        )}

        {/* Überfällig-Zone links von HEUTE (nur wenn es welche gibt). */}
        {hatUeberfaellig && (
          <>
            <rect
              x={X_UEBERF - 20}
              y={LANE_TOP - 2}
              width={X_HEUTE - (X_UEBERF - 20)}
              height={GLEISE.length * LANE_H}
              fill="rgba(217,119,87,0.06)"
            />
            <circle ref={pulsRef} cx={X_UEBERF - 12} cy={LANE_TOP + 4} r={4} fill={clayHex} />
            <text
              x={X_UEBERF - 6}
              y={LANE_TOP + GLEISE.length * LANE_H + 2}
              textAnchor="middle"
              fontSize="7"
              fill={clayHex}
              fontFamily="JetBrains Mono, ui-monospace, monospace"
            >
              ÜBERFÄLLIG
            </text>
          </>
        )}

        {/* Zeitachse mit ECHTEN Daten (Kevin: wann es genau abgeht). */}
        {[0, 7, 14, 30].map((tag) => {
          if (tag > horizont) return null;
          const x = X_HEUTE + (X_END - X_HEUTE) * clamp(tag / horizont, 0, 1);
          const d = new Date(heute);
          d.setDate(d.getDate() + tag);
          const datum = `${d.getDate()}.${d.getMonth() + 1}.`;
          const wtag = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"][d.getDay()];
          return (
            <g key={tag}>
              <line
                x1={x}
                y1={LANE_TOP - 2}
                x2={x}
                y2={LANE_TOP + GLEISE.length * LANE_H}
                stroke="rgba(245,241,232,0.10)"
                strokeWidth={tag === 0 ? 1.2 : 0.5}
              />
              <text
                x={x}
                y={LANE_TOP - 12}
                textAnchor="middle"
                fontSize="8"
                fill={tag === 0 ? "#f5f1e8" : "#a8a29a"}
                fontFamily="JetBrains Mono, ui-monospace, monospace"
                fontWeight={tag === 0 ? 700 : 400}
              >
                {tag === 0 ? "HEUTE" : `+${tag} T`}
              </text>
              <text
                x={x}
                y={LANE_TOP - 4}
                textAnchor="middle"
                fontSize="7.5"
                fill="#8a857c"
                fontFamily="JetBrains Mono, ui-monospace, monospace"
              >
                {wtag} {datum}
              </text>
            </g>
          );
        })}

        {/* 4 Firmen-Gleise: Label + Teilsumme links, Schiene, Lok am Ende. */}
        {GLEISE.map((g, gi) => {
          const y = LANE_TOP + gi * LANE_H + LANE_H / 2;
          const teil = summeProFirma[g.key] ?? 0;
          const lx = X_END + 20; // Lok zieht den Zug nach rechts (raus).
          return (
            <g key={g.key}>
              <line x1={X_UEBERF - 20} y1={y + 8} x2={X_END + 38} y2={y + 8} stroke="rgba(245,241,232,0.12)" strokeWidth={0.8} />
              <text x={X_LABEL} y={y + 3} fontSize="10" fill={g.hex} fontFamily="JetBrains Mono, ui-monospace, monospace" fontWeight={600}>
                {g.label}
              </text>
              {/* KONSOLIDIERTE Firmen-Summe an der Lok — "bei DrainX geht X ab". */}
              <text
                x={lx}
                y={y - 14}
                textAnchor="middle"
                fontSize="12"
                fill={teil > 0 ? "#f5f1e8" : "#6b7280"}
                fontFamily="JetBrains Mono, ui-monospace, monospace"
                fontWeight={700}
              >
                {teil > 0 ? `−${formatEurCompact(teil)}` : "—"}
              </text>
              {/* Diesel-Lok (Seitenansicht, fährt nach rechts). */}
              <g opacity={teil > 0 ? 0.95 : 0.3}>
                {/* Fahrgestell */}
                <rect x={lx - 15} y={y + 4} width={30} height={2} fill="rgba(11,11,12,0.7)" />
                {/* Führerhaus (hinten links, höher) */}
                <rect x={lx - 15} y={y - 10} width={11} height={14} rx={1.5} fill={g.hex} opacity={0.95} />
                {/* Fenster */}
                <rect x={lx - 13} y={y - 8} width={7} height={4} fill="rgba(11,11,12,0.8)" />
                {/* Motorvorbau (vorn rechts, niedriger) mit abgeschrägter Nase */}
                <path
                  d={`M ${lx - 4} ${y - 5} L ${lx + 11} ${y - 5} L ${lx + 15} ${y - 1} L ${lx + 15} ${y + 4} L ${lx - 4} ${y + 4} Z`}
                  fill={g.hex}
                  opacity={0.95}
                />
                {/* Zierstreifen */}
                <line x1={lx - 4} y1={y - 1} x2={lx + 14} y2={y - 1} stroke="#fff" strokeWidth={0.7} opacity={0.35} />
                {/* Scheinwerfer */}
                <circle cx={lx + 13} cy={y + 1.5} r={1.3} fill="#fde68a" opacity={0.9} />
                {/* 3 Räder */}
                {[-9, -1, 9].map((dx) => (
                  <circle key={dx} cx={lx + dx} cy={y + 7} r={2.6} fill="rgba(11,11,12,0.95)" stroke={g.hex} strokeWidth={0.7} opacity={0.9} />
                ))}
              </g>
            </g>
          );
        })}

        {/* Güterwagen: schlicht + Betrag darüber (entrümpelt). */}
        {wagen.map((w, i) => {
          const gi = GLEISE.findIndex((g) => g.key === w.gleis);
          if (gi < 0) return null;
          const y = LANE_TOP + gi * LANE_H + LANE_H / 2;
          const hex = GLEISE[gi].hex;
          // Wagen bleibt IMMER in Firmenfarbe — Überfällig markiert die Zone
          // links der HEUTE-Linie + gestrichelte Clay-Umrandung. (Vorher war
          // der Wagen selbst clay → mit InfraX-Rot verwechselbar.)
          const fill = hex;
          return (
            <g
              key={w.posten.id}
              ref={(el) => {
                wagenRefs.current[i] = el;
              }}
              style={{
                willChange: "transform",
                transformBox: "fill-box",
                transformOrigin: "center bottom",
              }}
            >
              {/* Kein Einzel-Betrag mehr am Wagen (überlappte) — die Zahl steht
                  konsolidiert pro Firma an der Lok; Einzelbetrag im Hover-Title.
                  Wagenbreite ∝ Betrag zeigt die Größe weiterhin visuell. */}
              {/* Kupplungshaken an beiden Enden. */}
              {([-1, 1] as const).map((end) => (
                <rect
                  key={`kup${end}`}
                  x={w.x + end * (w.breite / 2) - (end < 0 ? 3 : 0)}
                  y={y + 1}
                  width={3}
                  height={2}
                  rx={1}
                  fill={fill}
                  opacity={0.55}
                />
              ))}
              {/* Boxcar-Kasten (überfällig: gestrichelte Clay-Umrandung). */}
              <rect
                x={w.x - w.breite / 2}
                y={y - 8}
                width={w.breite}
                height={13}
                rx={2}
                fill={fill}
                opacity={0.92}
                stroke={w.ueberfaellig ? clayHex : "none"}
                strokeWidth={w.ueberfaellig ? 1.5 : 0}
                strokeDasharray={w.ueberfaellig ? "3 2" : undefined}
              />
              {/* Gewölbtes Dach (Güterwagen-Silhouette). */}
              <path
                d={`M ${w.x - w.breite / 2 + 1} ${y - 8} Q ${w.x} ${y - 12} ${w.x + w.breite / 2 - 1} ${y - 8} Z`}
                fill={fill}
                opacity={0.92}
              />
              <line
                x1={w.x - w.breite / 2 + 2}
                y1={y - 7.5}
                x2={w.x + w.breite / 2 - 2}
                y2={y - 7.5}
                stroke="#fff"
                strokeWidth={0.6}
                opacity={0.3}
              />
              {/* Schiebetür in der Mitte. */}
              <rect
                x={w.x - 6}
                y={y - 6}
                width={12}
                height={9}
                fill="none"
                stroke="rgba(11,11,12,0.45)"
                strokeWidth={0.6}
              />
              <line x1={w.x} y1={y - 6} x2={w.x} y2={y + 3} stroke="rgba(11,11,12,0.45)" strokeWidth={0.6} />
              {/* Untergestell (Solebar). */}
              <rect
                x={w.x - w.breite / 2 + 1}
                y={y + 4}
                width={w.breite - 2}
                height={2}
                fill="rgba(11,11,12,0.65)"
              />
              {/* Drehgestell: zwei Räder mit Reifen + Kreuzspeiche (rotieren ∝ Distanz). */}
              {([0, 1] as const).map((side) => {
                const cx = w.x + (side === 0 ? -1 : 1) * (w.breite / 2 - 5);
                const cy = y + 8;
                return (
                  <g
                    key={`rad${side}`}
                    ref={(el) => {
                      const rows = wheelRefs.current;
                      if (!rows[i]) rows[i] = [null, null];
                      rows[i][side] = el;
                    }}
                    style={{ transformBox: "fill-box", transformOrigin: "center" }}
                  >
                    <circle cx={cx} cy={cy} r={3} fill="rgba(11,11,12,0.95)" stroke={fill} strokeWidth={0.8} opacity={0.92} />
                    <circle cx={cx} cy={cy} r={1.1} fill={fill} opacity={0.7} />
                    <line x1={cx} y1={cy - 2.8} x2={cx} y2={cy + 2.8} stroke={fill} strokeWidth={0.5} opacity={0.7} />
                    <line x1={cx - 2.8} y1={cy} x2={cx + 2.8} y2={cy} stroke={fill} strokeWidth={0.5} opacity={0.7} />
                  </g>
                );
              })}
              <title>
                {`${GLEISE[gi].label} · ${formatEUR(String(w.posten.betrag_eur))} · ${
                  w.posten.titel ?? "Abgang"
                } · fällig ${w.posten.faellig_am} · ${w.posten.quelle}`}
              </title>
            </g>
          );
        })}
      </svg>

      {/* Abfahrtstafel: WANN geht WAS an WEN — kurz & knapp (Kevin 06.07.). */}
      <div className="mt-2 border-t border-white/5 pt-2">
        <div className="label-tracked mb-1 text-[10px]">Abfahrtstafel · nächste Abbuchungen</div>
        <ul className="space-y-0.5">
          {[...(data?.posten ?? [])]
            .sort((a, b) => a.faellig_am.localeCompare(b.faellig_am))
            .slice(0, 7)
            .map((p) => {
              const hex = FIRMA_COLORS[p.firma]?.hex ?? "#f5f1e8";
              const d = new Date(`${p.faellig_am}T00:00:00`);
              const wtag = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"][d.getDay()];
              const datum = `${wtag} ${d.getDate()}.${d.getMonth() + 1}.`;
              const ueberf = tageBis(p.faellig_am, heute) < 0;
              return (
                <li
                  key={p.id}
                  className="grid grid-cols-[68px_12px_1fr_auto] items-center gap-2 text-[11px]"
                >
                  <span className={`num-mono ${ueberf ? "text-signal-clay" : "text-leitstand-dim"}`}>
                    {ueberf ? "überfällig" : datum}
                  </span>
                  <span className="h-2 w-2 rounded-full" style={{ background: hex }} aria-hidden />
                  <span className="truncate text-leitstand-text/90">{p.titel ?? "Abgang"}</span>
                  <span
                    className={`num-mono font-semibold ${ueberf ? "text-signal-clay" : "text-leitstand-text"}`}
                  >
                    −{formatEurCompact(p.betrag_eur)}
                  </span>
                </li>
              );
            })}
        </ul>
      </div>
    </div>
  );
}

/** Statischer Rahmen für die 4 Daten-Kontrakt-Zustände (loading/error/leer). */
function Rahmen({ hint, leer = false }: { hint: string; leer?: boolean }) {
  return (
    <div
      aria-label="Abgangs-Radar Rangierbahnhof"
      className="flex min-h-[180px] items-center justify-center rounded-lg border border-[rgba(245,241,232,0.08)] bg-[rgba(255,255,255,0.03)]"
    >
      <span className={`num-mono text-[11px] ${leer ? "text-signal-green" : "text-leitstand-dim"}`}>
        {hint}
      </span>
    </div>
  );
}
